import React, { useState } from 'react';
import { FeatureOptions } from '../../phonemes/bucketByFeatures';
import { heightOfTongue, mannerOfArticulation, partOfTongue, placeOfArticulation, roundedness, voicing } from '../../phonemes/features';
import { chartMultiIpa, getAllIpa, IpaType } from '../../phonemes/ipa';
import { CellProps, FeatureChart } from '../chart/FeatureChart';
import { IpaList } from '../customRules/IpaList';
import { getFeatureString } from '../getFeatureString';
import { speakSegment } from '../speakSegment';

interface CompactIpaProps {
  readonly bolded: Set<IpaType>;
}

/**
 * Compactly show various sets of IPA phonemes, bolding selected ones
 */
export function CompactIpa(props: CompactIpaProps) {
  const [symbols] = useState(() => getAllIpa().ipa.concat(chartMultiIpa));
  const [hovered, setHovered] = useState('');
  const [features, setFeatures] = useState('');
  const [colsConsonants] = useState(() => placeOfArticulation.filter(f => f !== 'alveolo-palatal' && f !== 'epiglottal' && f !== 'labial-palatal'));
  const [cellProps] = useState<CellProps>({ bold: props.bolded, clicked: toClipboard, rightClicked: speakSegment, hovered: onHover });
  const rowStyle = { backgroundColor: '#eff2f7' };
  // 'whistled' is used just to create empty rows for separation
  let rows: FeatureOptions[] = mannerOfArticulation.filter(f => f !== 'lateral-flap' && f !== 'affricate' && f !== 'lateral-fricative');
  rows = rows.concat(['whistled', 'whistled', ['ejective', 'plosive'], ['ejective', 'fricative'], 'implosive', 'click']);

  function onHover(ipa: IpaType) {
    setHovered(ipa);
    setFeatures(getFeatureString(ipa));
  }

  return (<div style={{ width: 380 }}>
    <div style={{ display: 'grid', gridTemplateColumns: '170px 150px' }}>
      <div>
        <FeatureChart symbols={symbols} columns={partOfTongue} rows={heightOfTongue} cells={roundedness} empty={'•'}
          showColumnHeaders={false} showRowHeaders={false} cellProps={cellProps} rowStyle={rowStyle} />
      </div>
      <div>
        <br />
        <IpaList clicked={toClipboard} bolded={props.bolded} items={'ː , ̆ , ˈ ˌ'} hovered={onHover} /><br />
        <IpaList clicked={toClipboard} bolded={props.bolded} items={'˥ ˦ ˧ ˨ ˩ , ˥˩ ˩˥ , ˨˦˨ ˦˨˦'} hovered={onHover} /><br />
        <br />
        <IpaList clicked={toClipboard} rightClicked={speakSegment} bolded={props.bolded} items={'ẽ  ̃  , i̯ u̯  ̯  , ɚ ɝ a˞  ˞ '} hovered={onHover} /><br />
        <IpaList clicked={toClipboard} rightClicked={speakSegment} bolded={props.bolded} items={'l̩ ɫ̩ m̩ n̩ ŋ̩ r̩ ɹ̩ ̩  , ʰ ʲ ʷ'} hovered={onHover} /><br />
        <br />
        <IpaList clicked={toClipboard} rightClicked={speakSegment} bolded={props.bolded} items={'t͡s d͡z t̠͡ʃ d̠͡ʒ ʈ͡ʂ ɖ͡ʐ t͡ɕ d͡ʑ  ͡ '} hovered={onHover} /><br />
        <IpaList clicked={toClipboard} rightClicked={speakSegment} bolded={props.bolded} items={'ɕ ʑ , ɬ ɮ , ɥ ɺ ɫ ᴅ ɧ'} hovered={onHover} /><br />
      </div>
    </div>
    <br />

    <FeatureChart symbols={symbols} columns={colsConsonants} rows={rows} cells={voicing} empty={'•'}
      showColumnHeaders={false} showRowHeaders={false} cellProps={cellProps} rowStyle={rowStyle} />

    <br />
    <b>&nbsp;{`features of /${hovered}/:`}</b> {features}
  </div>);
}

function toClipboard(ipa: string) {
  if (ipa !== '•') {
    navigator.clipboard.writeText(ipa);
  }
}
